import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search as SearchIcon, Loader2, Users, Radio, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck, TrustBand } from "@/components/common/TrustBadge";
import { timeAgo } from "@/lib/format";

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Section({ title, count, children }) {
  if (!count) return null;
  return (
    <div className="mb-6">
      <div className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function RowShell({ onClick, testid, children }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 p-3 text-left transition-colors hover:bg-white/5" data-testid={testid}>
      {children}
    </button>
  );
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const debounced = useDebounced(q.trim(), 300);

  const { data, isFetching } = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => api.search(debounced),
    enabled: debounced.length >= 1,
  });

  const chats = data?.chats || [];
  const bloqs = data?.bloqs || [];
  const channels = data?.channels || [];
  const messages = data?.messages || [];
  const total = chats.length + bloqs.length + channels.length + messages.length;

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="search-page">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1 text-bloq-muted hover:text-white md:hidden" data-testid="search-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold">Search</h1>
        </div>

        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-bloq-muted" />
          {isFetching && <Loader2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-bloq-muted" />}
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people, bloqs, channels…"
            className="h-13 w-full rounded-2xl border border-white/12 bg-bloq-s1 pl-12 pr-10 text-[15px] text-white outline-none focus:border-bloq-mint/40"
            data-testid="global-search-input"
          />
        </div>

        <div className="mt-6">
          {debounced.length < 1 ? (
            <p className="py-14 text-center text-sm text-bloq-muted">Start typing to search across BLOQ Talk.</p>
          ) : !isFetching && total === 0 ? (
            <p className="py-14 text-center text-sm text-bloq-muted" data-testid="search-empty">No results for "{debounced}".</p>
          ) : (
            <>
              <Section title="People & chats" count={chats.length}>
                {chats.map((c) => (
                  <RowShell key={c.id} testid={`search-chat-${c.other?.username}`} onClick={() => navigate(`/app/chats/${c.id}`)}>
                    <Avatar user={c.other} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-semibold">{c.other?.display_name}</span>
                        {c.other?.verified && <VerifiedCheck size={12} />}
                        {c.other?.score_band && <TrustBand band={c.other.score_band} />}
                      </div>
                      <div className="truncate text-xs text-bloq-muted">@{c.other?.username} · {timeAgo(c.last_activity)}</div>
                    </div>
                  </RowShell>
                ))}
              </Section>

              <Section title="Messages" count={messages.length}>
                {messages.map((m, i) => (
                  <RowShell key={m.id || i} testid="search-message" onClick={() => m.conversation_id && navigate(`/app/chats/${m.conversation_id}`)}>
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 text-bloq-muted"><MessageCircle className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{m.text || m.snippet}</p>
                      <div className="text-xs text-bloq-muted">{timeAgo(m.created_at)}</div>
                    </div>
                  </RowShell>
                ))}
              </Section>

              <Section title="BLOQs" count={bloqs.length}>
                {bloqs.map((c) => (
                  <RowShell key={c.id} testid={`search-bloq-${c.id}`} onClick={() => navigate(`/app/bloqs/${c.id}`)}>
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-bloq-s2 text-lg">{c.emoji || <Users className="h-4 w-4" />}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold">{c.name}</div>
                      <div className="text-xs text-bloq-muted">{c.member_count} members{c.is_member ? " · joined" : ""}</div>
                    </div>
                  </RowShell>
                ))}
              </Section>

              <Section title="Channels" count={channels.length}>
                {channels.map((c) => (
                  <RowShell key={c.id} testid={`search-channel-${c.id}`} onClick={() => navigate(`/app/channels/${c.id}`)}>
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-bloq-s2 text-lg">{c.emoji || <Radio className="h-4 w-4" />}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-semibold">{c.name}</span>
                        {c.verified && <VerifiedCheck size={12} />}
                      </div>
                      <div className="text-xs text-bloq-muted">@{c.handle}{c.subscriber_count != null ? ` · ${c.subscriber_count} subscribers` : ""}</div>
                    </div>
                  </RowShell>
                ))}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
